import * as Sentry from "@sentry/react-native";
import { useCallback, useMemo, useRef, useState } from "react";
import type { ScrollView } from "react-native";

import {
    dismissValidationErrorApi,
    fetchSectionState,
    fetchValidation,
    type ValidationError
} from "@/lib/api";

type ActiveSheet = null | "validation" | "narrative" | "diagnosis" | "hub";

interface ErrorHighlight {
    questionId: string | null;
    tick: number;
}

interface UseValidationFlowOptions {
    visitId: string;
    sectionId: string;
    flushSaves: () => Promise<boolean>;
    /** All visible question IDs — used to validate that LLM-returned questionIds are real */
    visibleQuestionIds: string[];
    /**
     * Surface failures (e.g. dismiss API rejection) to the user via the
     * existing section-screen snackbar. Wired by the parent component;
     * `handleNextWithErrors` uses the same plumbing.
     */
    showSnackbar?: (msg: string) => void;
    /**
     * Latest server-clock `validationComputedAt` known to the parent (updated
     * from each `fetchSectionState` on focus). The Bug-10 recovery path uses
     * this to detect whether the server actually committed validation during
     * the current run when the response was lost in transit:
     *   - capture `prev = ref.current` at the start of `triggerValidation`
     *   - on fetch failure, refetch state; if `state.validationComputedAt`
     *     differs from `prev` → server committed during this run → display.
     *   - if it matches, the server didn't commit anything new → show failure.
     * This avoids comparing client-clock against server-clock (which would
     * leak clock-skew false-negatives for users with drifting device time).
     */
    lastValidationComputedAtRef?: React.MutableRefObject<string | null>;
}

interface UseValidationFlowReturn {
    // State
    validationErrors: ValidationError[];
    activeErrors: ValidationError[];
    errorsByQuestion: Map<string | null, ValidationError[]>;
    orphanErrors: ValidationError[];
    remainingCount: number;
    validationLoading: boolean;
    validationFailed: boolean;
    activeSheet: ActiveSheet;
    errorHighlight: ErrorHighlight;

    // Sheet management
    setActiveSheet: (sheet: ActiveSheet) => void;
    closeSheet: () => void;

    // Actions
    /**
     * Returns the post-run snapshot synchronously. Callers MUST read these
     * values rather than re-reading `validationFailed` / `remainingCount`
     * off the hook object after `await` — those are stale-closure reads
     * (React batches the hook's state writes into the next render and the
     * in-flight `await` callback's closure points at the prior render).
     *
     * `activeErrors` is the sanitized list after dismiss-set reset; if it
     * has length 0 AND `failed === false`, the section can transition to
     * "clear". Otherwise the caller stays in "errors" or "failed".
     */
    triggerValidation: () => Promise<
        | { failed: boolean; activeErrors: ValidationError[] }
        // The request was aborted (the RN left the section or a newer run
        // replaced it). Not a result: the caller must never treat it as clean.
        | { cancelled: true }
    >;
    cancel: () => void;
    dismissError: (index: number) => void;
    handleNextWithErrors: (
        scrollRef: React.RefObject<ScrollView | null>,
        questionYPositions: React.MutableRefObject<Map<string, number>>,
        showSnackbar: (msg: string) => void
    ) => void;
    handleGoToQuestions: () => void;
    resetForRevalidation: () => void;

    /**
     * Replace the current error set from outside (e.g. parent's section-state
     * focus refresh). Sanitizes against current visibleQuestionIds. Use this
     * instead of the old `initialErrors` constructor param so the parent owns
     * the lifecycle of when DB-persisted errors are loaded vs. when fresh LLM
     * results arrive.
     */
    syncErrorsFromServer: (errors: ValidationError[]) => void;

    // Guards
    hasTriggeredRef: React.MutableRefObject<boolean>;
}

// Wall-clock fallback for the iOS-suspended-fetch path. If iOS pauses the JS
// task mid-fetch, neither the catch nor the finally below may fire — without
// this fallback, `inFlightRef.current` stays `true` forever and every Retry
// tap silently no-ops. Matches the API client's DEFAULT_REQUEST_TIMEOUT_MS
// (120s) plus a 10s buffer so it never wins the race against a healthy
// network response.
const INFLIGHT_FALLBACK_MS = 130_000;

export function useValidationFlow({
    visitId,
    sectionId,
    flushSaves,
    visibleQuestionIds,
    showSnackbar,
    lastValidationComputedAtRef
}: UseValidationFlowOptions): UseValidationFlowReturn {
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationFailed, setValidationFailed] = useState(false);
    const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);
    const [errorHighlight, setErrorHighlight] = useState<ErrorHighlight>({
        questionId: null,
        tick: 0
    });

    const hasTriggeredRef = useRef(false);
    const abortRef = useRef<AbortController | null>(null);
    const inFlightRef = useRef(false);
    const visibleIdsSet = useMemo(() => new Set(visibleQuestionIds), [visibleQuestionIds]);

    // Validate that a questionId actually exists in this section
    const isValidQuestionId = useCallback(
        (id: string | null): boolean => {
            if (id == null) {
                return false;
            }
            return visibleIdsSet.has(String(id));
        },
        [visibleIdsSet]
    );

    // Sanitize errors from the LLM — treat invalid questionIds as null (orphan)
    const sanitizeErrors = useCallback(
        (errors: ValidationError[]): ValidationError[] => {
            return errors.map((err) => ({
                ...err,
                questionId: isValidQuestionId(err.questionId) ? String(err.questionId) : null
            }));
        },
        [isValidQuestionId]
    );

    // Sync errors from server-side state. Called by the parent's section-state
    // focus refresh whenever a fresh `state.validationErrors` arrives. The
    // previous `initializedRef` latch tried to "load once on mount" via an
    // initialErrors prop, but that latched on the first render with an empty
    // array (parent fetch hadn't resolved yet) and ignored subsequent updates,
    // so DB-persisted errors silently disappeared on remount. The Visit 127 R13
    // protection ("don't let a late DB resolve clobber fresh LLM results") is
    // now the parent's responsibility: parent only calls this on focus, not on
    // every render, and `triggerValidation` setting state directly is the only
    // mutation while the user is on the validation step.
    const syncErrorsFromServer = useCallback(
        (errors: ValidationError[]) => {
            setValidationErrors(sanitizeErrors(errors));
        },
        [sanitizeErrors]
    );

    // Derived: active errors (not dismissed)
    const activeErrors = useMemo(
        () => validationErrors.filter((e) => !e.id || !dismissedIds.has(e.id)),
        [validationErrors, dismissedIds]
    );

    // Derived: errors grouped by questionId
    const errorsByQuestion = useMemo(() => {
        const map = new Map<string | null, ValidationError[]>();
        for (const err of validationErrors) {
            if (err.id && dismissedIds.has(err.id)) {
                continue;
            }
            const key = err.questionId;
            const arr = map.get(key) ?? [];
            arr.push(err);
            map.set(key, arr);
        }
        return map;
    }, [validationErrors, dismissedIds]);

    // Derived: orphan errors (no questionId)
    const orphanErrors = useMemo(
        () => activeErrors.filter((e) => e.questionId == null),
        [activeErrors]
    );

    const remainingCount = activeErrors.length;

    // Trigger validation.
    //
    // ─── Behavior change (Bug 5 / "latest wins"): ───────────────────────
    // Previously this early-returned silently when `inFlightRef.current === true`
    // (first-wins). After the iOS-suspension class of bugs we observed in
    // production (LogRocket sessions for visit 205 + 4 prior incidents), that
    // first-wins behavior left the hook permanently locked when the previous
    // promise never resolved. New behavior: abort the prior controller and
    // claim the slot (latest-wins).
    //
    // ─── Per-controller ownership (Bug 5): ──────────────────────────────
    // Each run captures its own `myController`. The finally block only resets
    // shared state (`inFlightRef`, `validationLoading`) if `abortRef.current`
    // is still pointing at this run's controller. Without this, the prior
    // run's late finally would clobber the new run's `inFlightRef = true`,
    // letting a third concurrent call slip past the guard.
    //
    // ─── Wall-clock fallback (Bug 6): ───────────────────────────────────
    // setTimeout fires after `INFLIGHT_FALLBACK_MS` to force-reset state if
    // neither catch nor finally fired (iOS suspended the JS task mid-fetch).
    //
    // ─── Server-side recovery (Bug 10): ─────────────────────────────────
    // If the fetch throws, before showing "Validation unavailable" check
    // whether the server actually persisted a fresh validation run. Per
    // Ship 2's architectural change, the LLM result is committed to the DB
    // *before* the response is sent — so if the response was lost in transit
    // (refresh, background, network blip), the work isn't lost; it's
    // queryable via `fetchSectionState`.
    //
    // Critical: the test for "did the server commit during MY run?" is
    // whether `validationComputedAt` ADVANCED from the value we captured at
    // run start. An empty `validationErrors` array is a valid success state
    // (validation passed cleanly) — the freshness check is what
    // distinguishes "passed cleanly during this run" from "server has stale
    // state from before this run."
    //
    // We compare server-clock to server-clock (prev value vs new value), not
    // client-clock to server-clock — avoiding clock-skew false-negatives on
    // mobiles whose device time has drifted from the server.
    const triggerValidation = useCallback(async (): Promise<
        { failed: boolean; activeErrors: ValidationError[] } | { cancelled: true }
    > => {
        // Latest-wins: abort prior in-flight (if any) before claiming the slot.
        if (inFlightRef.current && abortRef.current && !abortRef.current.signal.aborted) {
            abortRef.current.abort();
        }
        inFlightRef.current = true;

        abortRef.current = new AbortController();
        const myController = abortRef.current;

        // Capture the server-clock validationComputedAt BEFORE the fetch so the
        // recovery path below can detect whether the server's value advanced
        // (i.e., the server committed during this run). Captured here, not at
        // recovery time, so concurrent focus refreshes can't shift the
        // reference under us mid-run.
        const prevComputedAt = lastValidationComputedAtRef?.current ?? null;

        setValidationLoading(true);
        setValidationFailed(false);
        setActiveSheet("validation");

        const fallback = setTimeout(() => {
            if (abortRef.current === myController && inFlightRef.current) {
                inFlightRef.current = false;
                setValidationLoading(false);
                setValidationFailed(true);
            }
        }, INFLIGHT_FALLBACK_MS);

        try {
            const flushed = await flushSaves();
            if (!flushed) {
                setValidationFailed(true);
                setValidationLoading(false);
                return { failed: true, activeErrors: [] };
            }

            let result;
            try {
                result = await fetchValidation(visitId, sectionId, {
                    signal: myController.signal
                });
            } catch (err) {
                if (myController.signal.aborted) {
                    return { cancelled: true };
                }
                // Server-side recovery (Bug 10). The freshness signal is
                // `validationComputedAt`: if it advanced from `prevComputedAt`,
                // the server committed a validation run during this trigger
                // (regardless of whether the result set is empty or not — an
                // empty active-error set IS a valid success state from a
                // clean validation run). If it didn't advance, the server
                // never persisted anything new for this run, so the failure
                // is real.
                const state = await fetchSectionState(visitId, sectionId).catch(() => null);
                if (
                    state &&
                    state.validationComputedAt &&
                    state.validationComputedAt !== prevComputedAt
                ) {
                    const recovered = sanitizeErrors(state.validationErrors);
                    setValidationErrors(recovered);
                    setDismissedIds(new Set());
                    if (lastValidationComputedAtRef) {
                        lastValidationComputedAtRef.current = state.validationComputedAt;
                    }
                    return { failed: false, activeErrors: recovered };
                }
                throw err;
            }

            if (myController.signal.aborted) {
                return { cancelled: true };
            }
            const sanitized = sanitizeErrors(result.validationResult.errors);
            setValidationErrors(sanitized);
            setDismissedIds(new Set()); // always clear on fresh validation
            // Keep the recovery baseline in sync without depending on a focus
            // refresh — protects back-to-back validates from comparing against
            // a stale prevComputedAt on the next run.
            if (lastValidationComputedAtRef && result.validationResult.computedAt) {
                lastValidationComputedAtRef.current = result.validationResult.computedAt;
            }
            return { failed: false, activeErrors: sanitized };
        } catch (err) {
            if (myController.signal.aborted) {
                return { cancelled: true };
            }
            console.warn("Validation failed:", err);
            setValidationErrors([]);
            setValidationFailed(true);
            return { failed: true, activeErrors: [] };
        } finally {
            clearTimeout(fallback);
            // OWNERSHIP CHECK (Bug 5): only the run that currently owns the
            // controller may reset shared state. Otherwise a previous run's
            // late finally clobbers a newer run's `inFlightRef = true`.
            if (abortRef.current === myController) {
                if (!myController.signal.aborted) {
                    setValidationLoading(false);
                }
                inFlightRef.current = false;
            }
        }
    }, [visitId, sectionId, flushSaves, sanitizeErrors]);

    const cancel = useCallback(() => {
        abortRef.current?.abort();
        inFlightRef.current = false;
    }, []);

    const closeSheet = useCallback(() => {
        setActiveSheet(null);
    }, []);

    // Dismiss a single error by its DB id.
    //
    // Bug 19 — was fire-and-forget; the catch only console.error'd, so if the
    // API DELETE failed (network blip), the mobile UI showed the error as
    // dismissed but the DB still had it active. Next validation re-fetched
    // the un-dismissed row. Now: optimistic UI update, but on failure roll it
    // back, snackbar the user, and capture to Sentry for visibility.
    const dismissError = useCallback(
        (errorId: number) => {
            // Optimistic
            setDismissedIds((prev) => {
                const next = new Set(prev);
                next.add(errorId);
                return next;
            });
            void (async () => {
                try {
                    await dismissValidationErrorApi(visitId, errorId);
                } catch (err) {
                    // Roll back the optimistic dismiss so the user sees the
                    // error again and can retry.
                    setDismissedIds((prev) => {
                        const next = new Set(prev);
                        next.delete(errorId);
                        return next;
                    });
                    showSnackbar?.("Couldn't dismiss — check your connection and try again.");
                    Sentry.captureException(err, {
                        tags: { component: "dismissError" },
                        extra: { visitId, errorId }
                    });
                    console.error("Failed to dismiss validation error from DB:", err);
                }
            })();
        },
        [visitId, showSnackbar]
    );

    // Handle "Next" when errors remain — snackbar + scroll + shake
    const handleNextWithErrors = useCallback(
        (
            scrollRef: React.RefObject<ScrollView | null>,
            questionYPositions: React.MutableRefObject<Map<string, number>>,
            showSnackbarLocal: (msg: string) => void
        ) => {
            showSnackbarLocal("Dismiss all items before continuing");

            // Find first undismissed error WITH a questionId (skip orphans)
            const firstWithQuestion = activeErrors.find((e) => e.questionId != null);
            if (firstWithQuestion && firstWithQuestion.questionId) {
                // Scroll to that question
                const y = questionYPositions.current.get(firstWithQuestion.questionId);
                if (y != null) {
                    scrollRef.current?.scrollTo({
                        y: Math.max(0, y - 20),
                        animated: true
                    });
                }
                // Red border + shake
                setErrorHighlight({
                    questionId: firstWithQuestion.questionId,
                    tick: Date.now()
                });
                setTimeout(() => setErrorHighlight({ questionId: null, tick: 0 }), 2500);
            } else {
                // Only orphan errors remain — scroll to top
                scrollRef.current?.scrollTo({ y: 0, animated: true });
            }
        },
        [activeErrors]
    );

    // Close the validation sheet and go back to questions
    const handleGoToQuestions = useCallback(() => {
        setActiveSheet(null);
    }, []);

    // Reset state for re-validation cycle
    const resetForRevalidation = useCallback(() => {
        hasTriggeredRef.current = false;
        setDismissedIds(new Set());
    }, []);

    return {
        validationErrors,
        activeErrors,
        errorsByQuestion,
        orphanErrors,
        remainingCount,
        validationLoading,
        validationFailed,
        activeSheet,
        errorHighlight,
        setActiveSheet,
        closeSheet,
        triggerValidation,
        cancel,
        dismissError,
        handleNextWithErrors,
        handleGoToQuestions,
        resetForRevalidation,
        syncErrorsFromServer,
        hasTriggeredRef
    };
}
